/*
 * The guided walkthrough: the whole plan on the left, a spotlight and an arrow
 * on the control you need next, and nothing moving until the appliance says the
 * step is done.
 *
 * Three decisions worth keeping.
 *
 * It highlights the LIVE control and lets the operator work it. A walkthrough
 * that clicked the switch would teach nothing and would be a second, unaudited
 * path to the same write. The point is that they end up knowing where it lives.
 *
 * It advances on EVIDENCE — and on the one field the step actually changes.
 * Watching the whole reading is what made the first version run itself to the
 * end in two seconds: a status body carries live counters, so every re-read
 * differed and every step "completed" the moment it appeared. A step with no
 * field to watch waits to be told instead of guessing.
 *
 * And the plan stays on screen. A walkthrough that shows only the current step
 * is a slideshow; showing all of them, with the done ones ticked, is what makes
 * it feel like being walked through something.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  FiCheck, FiX, FiArrowRight, FiCornerUpLeft, FiChevronRight, FiAlertCircle,
  FiLock, FiMessageCircle, FiPlay,
} from "react-icons/fi";
import {
  readTour, advanceTour, endTour, recordApplied, recordTourEnd,
  pauseTour, resumeTour, askAboutStep,
} from "./tourStore";
import { stepDone } from "./tourSteps";
import { ASSISTANT_NAME, VIEW_ASSISTANT, VIEW_CONSOLE, navigateTo, readView } from "../appView";

/* What the footer tells the operator to expect.
 *
 * It used to say "Set it, then continue." for anything with nothing to watch —
 * in front of a switch the panel had disabled, and on tour stops that exist
 * only to be read. Both describe a screen the operator is not looking at, and
 * being told to do the impossible is what makes a walkthrough feel broken. */
function watchLine(step, blocked) {
  if (blocked) return "Nothing to set here until then.";
  if (step.kind === "explain") return "Nothing to change — this one is to look at.";
  if (step.verify) return "Waiting for you — it moves on by itself.";
  return "Set it, then continue.";
}

const FIND_INTERVAL = 220;
const FIND_TIMEOUT = 8000;
const VERIFY_INTERVAL = 900;

/* How long the tick stays up before moving on, so the operator sees that what
   they just did registered rather than the panel jumping under them. */
const CONFIRM_MS = 1100;

export default function TourOverlay({ onPanel, nodeApiUrl }) {
  // Steps the appliance confirmed during this walkthrough.
  const appliedRef = useRef([]);
  const [tour, setTour] = useState(() => readTour());
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  // The panel's own reason this control will not move, "" when it will.
  const [blocked, setBlocked] = useState("");
  // null while running; on finishing, what the appliance actually shows.
  const [done, setDone] = useState(null);
  const noteRef = useRef(null);
  const [noteRect, setNoteRect] = useState(null);

  // A paused walkthrough has a position but nothing on screen: the operator
  // stepped away to ask something, and the console is theirs again until they
  // say otherwise. No step means every effect below sits still.
  const step = tour && !tour.paused ? tour.steps[tour.at] : null;

  /* Leaving the console by any route pauses the walkthrough: Back to Ada,
     the Ask Ada button, the browser's back button, the landing page. Only
     Back to Ada paused it before, so arriving by any other way found Ada
     with no sign of a walkthrough still under way. Read from the address
     rather than assumed, because a remount in place (React's development
     double mount, a node switch) is not a departure. */
  useEffect(() => () => {
    if (readView() !== VIEW_CONSOLE) pauseTour();
  }, []);

  useEffect(() => {
    if (step && onPanel) onPanel(step.panel);
  }, [step, onPanel]);

  /* Track the target. Scroll and layout both move it, and an arrow that lags
     its control points at nothing. */
  useEffect(() => {
    if (!step) return undefined;
    setMissing(false);
    setRect(null);
    setConfirmed(false);
    setBlocked("");

    let raf = 0;
    let waited = 0;
    let stop = false;
    // Opening is a React state change, so the control appears a render later.
    // Without this the retry would click the disclosure a second time and shut
    // it again, and the walkthrough would sit there toggling a panel.
    const opened = new Set();

    const find = () => {
      if (stop) return;
      const sel = step.target
        ? `[data-tour="${step.target}"]`
        : `[data-tour="nav.${step.panel}"]`;
      let el = document.querySelector(sel);

      // A control behind a disclosure is not missing, it is closed. Panels say
      // which controls they hide; open it and look again, rather than telling
      // the operator a switch is "not on screen" while they are staring at the
      // panel that contains it.
      if (!el && step.target) {
        const opener = document.querySelector(`[data-tour-opens~="${step.target}"]`);
        if (opener && !opened.has(opener)) {
          opened.add(opener);
          opener.click();
        }
      }

      if (!el) {
        waited += FIND_INTERVAL;
        if (waited >= FIND_TIMEOUT) setMissing(true);
        else setTimeout(find, FIND_INTERVAL);
        return;
      }
      // Open the control's own detail. These panels put the finer settings —
      // the ones a proposal usually changes — behind a per-control disclosure,
      // so an operator taken to a switch never sees what sits under it. Opening
      // it is the difference between being shown a toggle and being shown what
      // the toggle governs.
      const inner = el.querySelector('[aria-expanded="false"]');
      if (inner && !opened.has(inner)) {
        opened.add(inner);
        inner.click();
      }

      el.scrollIntoView({ block: "center", behavior: "smooth" });
      const track = () => {
        if (stop) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        // Read with the geometry rather than once on arrival: an interlock can
        // clear while the operator stands here, because the thing it waits on
        // is a control they can go and turn on.
        const why = el.getAttribute("data-tour-blocked") || "";
        setBlocked((prev) => (prev === why ? prev : why));
        if (noteRef.current) {
          const n = noteRef.current.getBoundingClientRect();
          setNoteRect({ top: n.top, left: n.left, width: n.width, height: n.height });
        }
        raf = requestAnimationFrame(track);
      };
      track();
    };
    find();

    return () => { stop = true; cancelAnimationFrame(raf); };
  }, [step]);

  /* Ending the walkthrough re-reads the appliance for every step that named a
     field, rather than assuming the operator did them.
     
     The first version congratulated them unconditionally: skip a step and it
     still said "that is the whole change — it is live now" over a switch that
     was plainly still off. A walkthrough that reports success it did not verify
     is worse than one that says nothing. */
  const finish = useCallback(async (finished) => {
    const readings = new Map();
    const outstanding = [];
    // What the card that started it will say about each step afterwards:
    // done or not where the appliance was read, nothing where there was
    // nothing to check.
    const results = [];

    for (const s of finished.steps) {
      if (!s.verify || !s.verifyPath || !nodeApiUrl) {
        results.push({ id: s.id, title: s.title, done: null });
        continue;
      }
      if (!readings.has(s.verifyPath)) {
        let doc = null;
        try {
          const url = nodeApiUrl(s.verifyPath);
          if (url) {
            const r = await fetch(url, { credentials: "include" });
            if (r.ok) doc = await r.json();
          }
        } catch {
          /* unreadable — reported as outstanding rather than assumed done */
        }
        readings.set(s.verifyPath, doc);
      }
      const done = stepDone(s, readings.get(s.verifyPath));
      if (!done) outstanding.push(s.title);
      results.push({ id: s.id, title: s.title, done });
    }

    recordTourEnd(finished, results);
    endTour();
    recordApplied(appliedRef.current);
    setTour(null);
    setDone({ outstanding });
  }, [nodeApiUrl]);

  const next = useCallback(() => {
    setTour((t) => {
      if (!t) return t;
      const at = t.at + 1;
      if (at >= t.steps.length) { finish(t); return t; }
      return advanceTour(at);
    });
  }, [finish]);

  /* Watch the one setting this step changes. Anything else in the reading is
     free to move without meaning the operator did anything. */
  useEffect(() => {
    if (!step || !step.verifyPath || !step.verify || !nodeApiUrl || confirmed) return undefined;
    let stop = false;

    const tick = async () => {
      if (stop) return;
      const url = nodeApiUrl(step.verifyPath);
      if (url) {
        try {
          const r = await fetch(url, { credentials: "include" });
          if (r.ok && stepDone(step, await r.json())) {
            setConfirmed(true);
            // Working the control by hand IS applying the change. Recording it
            // is what stops the operator coming back to a card still asking
            // them to confirm something they have already done — and it is what
            // puts the change in the appliance's notebook, which only ever
            // learns about one through a resolved proposal.
            appliedRef.current = [...appliedRef.current, step];
            markApplied(nodeApiUrl, step);
            return;
          }
        } catch {
          /* transient — keep watching */
        }
      }
      if (!stop) setTimeout(tick, VERIFY_INTERVAL);
    };
    tick();

    return () => { stop = true; };
  }, [step, nodeApiUrl, confirmed]);

  // Hold the tick on screen briefly so the operator sees it land.
  useEffect(() => {
    if (!confirmed) return undefined;
    const t = setTimeout(next, CONFIRM_MS);
    return () => clearTimeout(t);
  }, [confirmed, next]);

  /* Quit is the only thing that throws the walkthrough away. Leaving for the
     assistant keeps it — see pauseTour. */
  const quit = () => { endTour(); setTour(null); setDone(null); };

  const leaveForAda = useCallback((question) => {
    if (question) askAboutStep(question);
    // A finished walkthrough has already ended; pausing it is a no-op then.
    setTour(pauseTour());
    setDone(null);
    navigateTo(VIEW_ASSISTANT, { from: "assistant" });
  }, []);

  const backToAda = () => leaveForAda(null);

  /* The question is written here, not by the model and not by the operator from
     a blank box, because the useful version names the step and the interlock —
     which is precisely what someone standing in front of a locked switch cannot
     be expected to phrase. It arrives in the composer to send or edit; the
     assistant is offered the question, never asked it on their behalf. */
  const askAda = () => {
    if (!step) return;
    const pos = tour.at + 1;
    leaveForAda(
      blocked
        ? `In the walkthrough, step ${pos} ("${step.title}") is locked — ${blocked}. ` +
          `What has to be turned on first, and why does it work in that order?`
        : `In the walkthrough, about step ${pos}, "${step.title}" — can you tell me more?`
    );
  };

  if (done) {
    const left = done.outstanding;
    return (
      <div className={`tour-done${left.length ? " is-partial" : ""}`} role="status">
        <Styles />
        {left.length ? (
          <>
            <FiAlertCircle size={16} />
            <span>
              {left.length === 1
                ? <><strong>{left[0]}</strong> is still not set.</>
                : <><strong>{left.length} of these</strong> are still not set.</>}{" "}
              The change is still queued — you can Apply it in {ASSISTANT_NAME}.
            </span>
          </>
        ) : (
          <>
            <FiCheck size={16} />
            <span>That is the whole change — confirmed live on the appliance.</span>
          </>
        )}
        <button onClick={backToAda}>Back to {ASSISTANT_NAME}</button>
        <button onClick={() => setDone(null)}>Close</button>
      </div>
    );
  }
  /* Paused: the plan is kept but nothing is drawn over the console. A pill
     rather than nothing, because a walkthrough the operator cannot see is one
     they cannot get back to — and going by way of the assistant to resume
     something they paused from the assistant is a circle. */
  if (!step) {
    if (!tour || !tour.paused) return null;
    const at = tour.steps[tour.at];
    return (
      <div className="tour-paused" role="status">
        <Styles />
        <span className="tour-paused-txt">
          Walkthrough paused · step {tour.at + 1} of {tour.steps.length}
          <em>{at ? at.title : ""}</em>
        </span>
        <button className="tour-paused-go" onClick={() => setTour(resumeTour())}>
          <FiPlay size={12} /> Resume
        </button>
        <button className="tour-paused-x" onClick={quit} aria-label="Discard walkthrough">
          <FiX size={14} />
        </button>
      </div>
    );
  }

  const total = tour.steps.length;
  const pos = tour.at + 1;

  return (
    <div className="tour-root">
      <Styles />

      {rect && (
        <>
          <div className="tour-shade" style={{ inset: "0 0 auto 0", height: Math.max(0, rect.top - 8) }} />
          <div className="tour-shade" style={{ top: rect.top + rect.height + 8, left: 0, right: 0, bottom: 0 }} />
          <div className="tour-shade" style={{ top: rect.top - 8, left: 0, width: Math.max(0, rect.left - 8), height: rect.height + 16 }} />
          <div className="tour-shade" style={{ top: rect.top - 8, left: rect.left + rect.width + 8, right: 0, height: rect.height + 16 }} />
          <div className={`tour-ring${confirmed ? " is-done" : ""}`} style={{
            top: rect.top - 8, left: rect.left - 8,
            width: rect.width + 16, height: rect.height + 16,
          }} />
        </>
      )}

      {/* A drawn line from the instruction to the control it is about, so there
          is no question which switch is meant. */}
      {rect && noteRect && <Connector from={noteRect} to={rect} done={confirmed} />}

      <aside className="tour-plan" aria-label="Walkthrough steps">
        <div className="tour-plan-head">
          <span>The change, step by step</span>
          <button className="tour-quit" onClick={quit} aria-label="End walkthrough"><FiX size={15} /></button>
        </div>
        <ol className="tour-plan-list">
          {tour.steps.map((s, i) => (
            <li key={s.id}
                className={i < tour.at || (i === tour.at && confirmed) ? "is-done" : i === tour.at ? "is-now" : ""}>
              <span className="tour-plan-mark">
                {i < tour.at || (i === tour.at && confirmed) ? <FiCheck size={11} /> : i + 1}
              </span>
              <span className="tour-plan-txt">
                <strong>{s.title}</strong>
                <em>{s.panelLabel}</em>
              </span>
            </li>
          ))}
        </ol>
        <button className="tour-back" onClick={backToAda}>
          <FiCornerUpLeft size={13} /> Back to {ASSISTANT_NAME}
        </button>
      </aside>

      <div ref={noteRef}
           className={`tour-note${rect ? "" : " tour-note--centred"}`}
           style={rect ? notePosition(rect, noteRect && noteRect.height) : undefined}>
        <div className="tour-note-head">
          <span className="tour-count">Step {pos} of {total}</span>
        </div>

        <div className="tour-title">{step.title}</div>

        {/* The assistant's own words about this control. The label above says
            what it is; this says why it is being changed here, today — which is
            the half no panel can carry. */}
        {step.why && !missing && (
          <p className={`tour-why${step.instruction ? "" : " is-lead"}`}>
            <span className="tour-why-mark" aria-hidden="true" />
            {step.why}
          </p>
        )}

        {missing ? (
          <p className="tour-body">
            This control is on the <strong>{step.panelLabel}</strong> panel. It is not on
            screen — open that panel and look for {step.title.toLowerCase()}.
          </p>
        ) : (
          <>
            {/* The panel refuses this control until something else is set. Saying
                so — and saying what — is the difference between a walkthrough
                that has stalled and one that is explaining an interlock. */}
            {blocked && (
              <p className="tour-locked">
                <FiLock size={12} />
                <span>Locked here — {blocked}.</span>
              </p>
            )}
            {step.instruction && !blocked && <p className="tour-body">{step.instruction}</p>}
            {!step.target && (
              <p className="tour-hint">
                <FiChevronRight size={12} /> It lives in <strong>{step.panelLabel}</strong>.
              </p>
            )}
          </>
        )}

        <div className="tour-foot">
          {confirmed ? (
            <span className="tour-ok"><FiCheck size={13} /> Done — that one is live.</span>
          ) : (
            <span className="tour-watch">{watchLine(step, blocked)}</span>
          )}
          <button className="tour-ask" onClick={askAda}>
            <FiMessageCircle size={12} /> Ask {ASSISTANT_NAME}
          </button>
          {/* Never "Finish" on a step that has not happened — that reads as
              "complete this for me" and it does no such thing. */}
          <button className="tour-next" onClick={next}>
            {confirmed || !step.verify
              ? (pos === total ? "Done" : "Next")
              : (pos === total ? "Skip and close" : "Skip this")}
            <FiArrowRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* Tell the appliance the operator made this change, so the queued proposal
   stops waiting on a confirmation that has already happened. Best-effort: a
   failure here leaves the card actionable, which is the safe way to be wrong. */
function markApplied(nodeApiUrl, step) {
  if (!nodeApiUrl || !step.id) return;
  const url = nodeApiUrl(`/agent/proposals/${encodeURIComponent(step.id)}/resolve`);
  if (!url) return;
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "applied" }),
  }).catch(() => {});
}

/* An elbow from the callout to the control, ending in an arrowhead. Drawn as
   one SVG over the page so it can cross any layout. */
function Connector({ from, to, done }) {
  const start = {
    x: from.left + from.width / 2,
    y: from.top < to.top ? from.top + from.height : from.top,
  };
  const end = {
    x: to.left + to.width / 2,
    y: from.top < to.top ? to.top - 10 : to.top + to.height + 10,
  };
  const midY = (start.y + end.y) / 2;
  const d = `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;
  const head = from.top < to.top
    ? `${end.x - 6},${end.y - 8} ${end.x + 6},${end.y - 8} ${end.x},${end.y + 2}`
    : `${end.x - 6},${end.y + 8} ${end.x + 6},${end.y + 8} ${end.x},${end.y - 2}`;

  return (
    <svg className="tour-arrow" aria-hidden="true">
      {/* pathLength normalises the curve to 1 so the draw-in works without
          measuring it: the dash is the whole line, and the offset retracts. */}
      <path d={d} pathLength="1" className={done ? "is-done" : ""} />
      <polygon points={head} className={done ? "is-done" : ""} />
    </svg>
  );
}

/* Where the instruction sits.
 *
 * Never over the control it is about, and never over the panel region the
 * operator is being asked to read — an instruction that hides its own subject
 * is worse than one that is slightly further away. Right of the control first,
 * since console panels are left-weighted and there is usually room; then below,
 * then above, then left. */
const NOTE_W = 340;
const NOTE_H = 200;
const GAP = 26;

/* Where the callout sits, given the control it points at and its OWN measured
   height.

   The height used to be the 200px guess above. A stop whose note runs long —
   several sentences about what the control costs — is half again that, so every
   clamp here was computed against a box smaller than the one being placed, and
   the bottom of the text ended up under the bottom of the window with no way to
   scroll to it. The note is measured every frame for the connector line
   already; this uses that number, and falls back to the guess only for the
   first frame, before there is anything to measure. */
export function notePosition(rect, noteH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Never taller than the window itself; the note scrolls inside past that.
  const h = Math.min(noteH || NOTE_H, vh - 32);
  const clampTop = (t) => Math.min(Math.max(16, t), Math.max(16, vh - h - 16));
  const midTop = clampTop(rect.top + rect.height / 2 - h / 2);

  if (vw - (rect.left + rect.width) > NOTE_W + GAP) {
    return { top: midTop, left: rect.left + rect.width + GAP };
  }
  const centred = Math.min(
    Math.max(16, rect.left + rect.width / 2 - NOTE_W / 2),
    Math.max(16, vw - NOTE_W - 16)
  );
  if (vh - (rect.top + rect.height) > h + GAP) {
    return { top: rect.top + rect.height + GAP, left: centred };
  }
  if (rect.top > h + GAP) {
    return { top: rect.top - h - GAP, left: centred };
  }
  if (rect.left > NOTE_W + GAP) {
    return { top: midTop, left: rect.left - NOTE_W - GAP };
  }
  // Nowhere clear to put it: sit it against the bottom of the window, still
  // wholly on screen.
  return { top: clampTop(vh - h - 24), left: centred };
}

const Styles = () => (
  <style>{`
.tour-root{position:fixed;inset:0;z-index:4000;pointer-events:none}
.tour-shade{position:fixed;background:rgba(8,11,20,.60);pointer-events:auto}
.tour-ring{
  position:fixed;border-radius:14px;pointer-events:none;
  border:2px solid #38BDF8;box-shadow:0 0 0 4px rgba(56,189,248,.20),0 0 30px rgba(56,189,248,.45);
  animation:tourpulse 1.9s ease-in-out infinite;
}
.tour-ring.is-done{border-color:#34D399;box-shadow:0 0 0 4px rgba(52,211,153,.22),0 0 30px rgba(52,211,153,.5);animation:none}
@keyframes tourpulse{
  0%,100%{box-shadow:0 0 0 4px rgba(56,189,248,.20),0 0 26px rgba(56,189,248,.40)}
  50%{box-shadow:0 0 0 8px rgba(56,189,248,.08),0 0 36px rgba(56,189,248,.62)}
}

.tour-arrow{position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:4001}
.tour-arrow path{
  fill:none;stroke:#38BDF8;stroke-width:2.5;stroke-linecap:round;
  stroke-dasharray:1;stroke-dashoffset:1;
  animation:tourdraw .6s cubic-bezier(.4,0,.2,1) forwards;
}
.tour-arrow polygon{fill:#38BDF8;opacity:0;animation:tourhead .3s ease-out .5s forwards}
.tour-arrow path.is-done{stroke:#34D399}
.tour-arrow polygon.is-done{fill:#34D399}
@keyframes tourdraw{to{stroke-dashoffset:0}}
@keyframes tourhead{to{opacity:1}}

/* The plan, pinned left: every step, what is done, what is next. */
/* Bottom-left: clear of the sidebar's navigation, clear of the panel content,
   and in the corner the eye returns to between steps. */
.tour-plan{
  position:fixed;bottom:22px;left:22px;width:250px;z-index:4002;pointer-events:auto;
  background:#11141F;border:1px solid rgba(148,163,184,.22);border-radius:14px;
  padding:14px;box-shadow:0 22px 60px rgba(0,0,0,.55);
  font:400 13px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;color:#E2E8F0;
}
.tour-plan-head{
  display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:12px;
  font-size:11px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#94A3B8;
}
.tour-plan-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:3px}
.tour-plan-list li{display:flex;gap:10px;align-items:flex-start;padding:8px;border-radius:9px;color:#8891AC}
.tour-plan-list li.is-now{background:rgba(56,189,248,.10);color:#E2E8F0}
.tour-plan-list li.is-done{color:#6B7392}
.tour-plan-mark{
  flex:none;width:19px;height:19px;border-radius:50%;display:grid;place-items:center;
  border:1px solid rgba(148,163,184,.35);font-size:10.5px;font-weight:700;margin-top:1px;
}
.tour-plan-list li.is-now .tour-plan-mark{border-color:#38BDF8;color:#38BDF8;background:rgba(56,189,248,.14)}
.tour-plan-list li.is-done .tour-plan-mark{border-color:#34D399;color:#34D399;background:rgba(52,211,153,.14)}
.tour-plan-txt{display:flex;flex-direction:column;gap:2px;min-width:0}
.tour-plan-txt strong{font-weight:600;font-size:13px}
.tour-plan-txt em{font-style:normal;font-size:11px;color:#6B7392}
.tour-back{
  margin-top:12px;width:100%;display:inline-flex;align-items:center;justify-content:center;gap:7px;
  cursor:pointer;border:1px solid rgba(148,163,184,.3);background:transparent;border-radius:9px;
  padding:9px;font:inherit;font-size:12.5px;font-weight:600;line-height:1;color:#CBD5E1;
}
.tour-back:hover{border-color:#67E8F9;color:#67E8F9}

.tour-note{
  position:fixed;width:340px;pointer-events:auto;z-index:4003;
  background:#11141F;border:1px solid rgba(56,189,248,.45);border-radius:14px;
  padding:15px 16px 13px;color:#E2E8F0;
  box-shadow:0 22px 60px rgba(0,0,0,.55);
  font:400 14px/1.6 system-ui,-apple-system,"Segoe UI",sans-serif;
  /* The backstop to notePosition's arithmetic: a note longer than the window
     scrolls inside itself instead of running off the bottom of it. */
  max-height:calc(100vh - 32px);
  overflow-y:auto;
  overscroll-behavior:contain;
  scrollbar-width:thin;
  scrollbar-color:rgba(148,163,184,.3) transparent;
}
.tour-note::-webkit-scrollbar{width:5px}
.tour-note::-webkit-scrollbar-track{background:transparent}
.tour-note::-webkit-scrollbar-thumb{background:rgba(148,163,184,.3);border-radius:3px}
.tour-note--centred{top:50%;left:50%;transform:translate(-50%,-50%)}
.tour-note-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
.tour-count{
  font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
  color:#BAE6FD;background:rgba(56,189,248,.16);border-radius:6px;padding:3px 9px;
}
.tour-quit{background:none;border:0;color:#94A3B8;cursor:pointer;padding:2px;display:flex}
.tour-quit:hover{color:#F1F5F9}
.tour-title{font-size:16px;font-weight:650;color:#F8FAFC;margin-bottom:5px}
.tour-body{margin:0;color:#CBD5E1}
/* On an explanation stop there is nothing to do, so the reason IS the body and
   carries the weight the instruction would have. */
.tour-why.is-lead{margin-top:2px;font-size:14px;color:#DCEEFB}
.tour-why{
  position:relative;margin:11px 0 0;padding:11px 13px 11px 15px;
  border-radius:10px;background:rgba(56,189,248,.08);
  border:1px solid rgba(56,189,248,.22);
  font-size:13.5px;line-height:1.6;color:#BAE6FD;
}
.tour-why-mark{
  position:absolute;left:0;top:11px;bottom:11px;width:2px;border-radius:2px;
  background:linear-gradient(180deg,#67E8F9,#818CF8);
}
.tour-hint{margin:8px 0 0;font-size:13px;color:#94A3B8;display:flex;align-items:center;gap:5px}

/* Amber, not red: an interlock is the panel working as designed, not a fault. */
.tour-locked{
  margin:10px 0 0;padding:9px 11px;border-radius:9px;
  background:rgba(251,191,36,.10);border:1px solid rgba(251,191,36,.34);
  font-size:13px;line-height:1.5;color:#FDE68A;
  display:flex;align-items:flex-start;gap:7px;
}
.tour-locked svg{flex:none;margin-top:2px}

.tour-foot{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:14px}
.tour-watch{font-size:12px;color:#7DD3FC;flex:1}
.tour-ok{font-size:12.5px;color:#6EE7B7;flex:1;display:flex;align-items:center;gap:6px;font-weight:600}
.tour-ask{
  display:inline-flex;align-items:center;gap:6px;cursor:pointer;flex:none;
  border:1px solid rgba(129,140,248,.42);background:transparent;border-radius:8px;
  padding:7px 11px;font:inherit;font-size:12.5px;font-weight:600;line-height:1;color:#C7D2FE;
}
.tour-ask:hover{border-color:#818CF8;color:#E0E7FF;background:rgba(129,140,248,.12)}
.tour-next{
  display:inline-flex;align-items:center;gap:6px;cursor:pointer;flex:none;
  border:1px solid rgba(148,163,184,.4);background:transparent;border-radius:8px;
  padding:7px 12px;font:inherit;font-size:13px;font-weight:600;line-height:1;color:#CBD5E1;
}
.tour-next:hover{border-color:#67E8F9;color:#67E8F9}

/* Paused: small, corner-anchored, and not covering anything. The console
   belongs to the operator again while the walkthrough waits. */
.tour-paused{
  position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:4003;
  display:flex;align-items:center;gap:12px;max-width:min(560px,92vw);
  background:#11141F;border:1px solid rgba(103,232,249,.38);border-radius:999px;
  padding:9px 10px 9px 18px;
  box-shadow:0 14px 40px rgba(0,0,0,.45);
}
.tour-paused-txt{
  font:600 12.5px/1.3 system-ui,-apple-system,sans-serif;color:#94A3B8;
  display:flex;flex-direction:column;gap:2px;min-width:0;
}
.tour-paused-txt em{
  font-style:normal;font-weight:600;color:#E2E8F0;font-size:13px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
}
.tour-paused-go{
  flex:none;display:inline-flex;align-items:center;gap:6px;cursor:pointer;
  border:0;border-radius:999px;padding:8px 15px;font:inherit;font-size:12.5px;font-weight:700;line-height:1;color:#0B0C11;
  background:linear-gradient(135deg,#67E8F9,#818CF8);
}
.tour-paused-go:hover{filter:brightness(1.06)}
.tour-paused-x{
  flex:none;background:none;border:0;color:#64748B;cursor:pointer;padding:4px;display:grid;place-items:center;
}
.tour-paused-x:hover{color:#CBD5E1}

.tour-done{
  position:fixed;left:50%;bottom:28px;transform:translateX(-50%);z-index:4003;
  display:flex;align-items:center;gap:11px;max-width:min(680px,92vw);
  background:#11141F;border:1px solid rgba(52,211,153,.5);border-radius:12px;
  padding:13px 17px;color:#A7F3D0;font:500 14px/1.45 system-ui,-apple-system,sans-serif;
  box-shadow:0 18px 50px rgba(0,0,0,.5);
}
.tour-done.is-partial{border-color:rgba(251,191,36,.55);color:#FDE68A}
.tour-done strong{font-weight:700}
.tour-done button{
  background:none;border:0;color:#94A3B8;cursor:pointer;font:inherit;font-size:13px;font-weight:600;line-height:1;padding:0 0 0 6px;
}
.tour-done button:hover{color:#F1F5F9}

@media (max-width:900px){
  .tour-plan{display:none}
  .tour-note{left:50%!important;transform:translateX(-50%);width:min(340px,92vw)}
}
@media (prefers-reduced-motion:reduce){
  .tour-ring{animation:none}
  .tour-arrow path{animation:none;stroke-dashoffset:0}
  .tour-arrow polygon{animation:none;opacity:1}
}
`}</style>
);
